import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { InputComponent } from '../../../../ui-components/lib/input/input.component';
import { ValidationMessageComponent } from '../../../../ui-components/lib/validation-message/validation-message.component';
import {
  EyeHiddenIconComponent,
  LockIconComponent,
  ViewIconComponent,
} from '@kuiper/icons';
import { passwordValidation, regexVariables } from '../../constants/regex.constant';

@Component({
  selector: 'lib-change-password',
  standalone: true,
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    InputComponent,
    ValidationMessageComponent,
    LockIconComponent,
    ViewIconComponent,
    EyeHiddenIconComponent,
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ChangePasswordComponent implements OnDestroy {
  isSubmitting = input(false);
  formSubmit = output<FormGroup>();
  discarded = output<void>();

  showCurrentPassword = false;
  showPassword = false;
  showConfirmationPassword = false;
  changePasswordForm = signal<FormGroup>(new FormGroup({}));
  passwordValidationStatus = {
    length: false,
    upperCase: false,
    lowerCase: false,
    number: false,
    specialChar: false,
    touched: false,
  };

  private translocoService = inject(TranslocoService);
  private subscription = new Subscription();

  constructor() {
    this.initChangePasswordForm();
  }

  initChangePasswordForm() {
    this.changePasswordForm.set(
      new FormGroup(
        {
          current_password: new FormControl('', [Validators.required]),
          new_password: new FormControl('', [
            Validators.required,
            Validators.pattern(regexVariables.password),
          ]),
          new_password_confirmation: new FormControl('', Validators.required),
        },
        {
          validators: this.matchingPasswords(
            'new_password',
            'new_password_confirmation'
          ),
        }
      )
    );

    const passwordControl = this.changePasswordForm().get('new_password');

    this.subscription.add(
      passwordControl?.valueChanges.subscribe(new_password => {
        this.validatePassword(new_password);
        this.passwordValidationStatus.touched = passwordControl.touched;
      })
    );
  }

  matchingPasswords(
    passwordKey: string,
    passwordConfirmationKey: string
  ): ValidatorFn {
    return (control: AbstractControl): any => {
      const formGroup = control as FormGroup;
      const password = formGroup.controls[passwordKey];
      const confirmPassword = formGroup.controls[passwordConfirmationKey];
      if (
        password &&
        confirmPassword &&
        password.value !== confirmPassword.value
      ) {
        confirmPassword.setErrors({
          ...confirmPassword.errors,
          mismatchPassword: true,
        });
        return { mismatchPassword: true };
      }

      if (confirmPassword && confirmPassword.hasError('mismatchPassword')) {
        const { ...otherErrors } = confirmPassword.errors || {};
        const remainingErrors =
          Object.keys(otherErrors).length > 0 ? otherErrors : null;
        confirmPassword.setErrors(remainingErrors);
      }

      return null;
    };
  }

  validatePassword(new_password: string) {
    if (new_password) {
      this.passwordValidationStatus = {
        ...this.passwordValidationStatus,
        length: new_password.length >= passwordValidation.minLength,
        upperCase: passwordValidation.hasUpperCase.test(new_password),
        lowerCase: passwordValidation.hasLowerCase.test(new_password),
        number: passwordValidation.hasNumber.test(new_password),
        specialChar: passwordValidation.hasSpecialChar.test(new_password),
      };
    }
  }

  submit() {
    if (this.changePasswordForm().valid && !this.isSubmitting()) {
      this.formSubmit.emit(this.changePasswordForm());
    }
  }

  discard() {
    this.discarded.emit();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
